import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../../components/SEOHelmet';
import { motion } from 'framer-motion';
import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactSection from '../shipping-information/components/ContactSection';

import QuoteCalculator from '../shipping-information/components/QuoteCalculator';
import NextDepartureCard from './components/NextDepartureCard';
import PromoSection from './components/PromoSection';

import ChatWidget from '../../components/ui/ChatWidget';
import { nextDepartureService } from '../../services/nextDepartureService';
import PromoStickBanner from '../../components/ui/PromoStickBanner';
import { containerService } from '../../services/containerService';

const EnhancedSEOOptimizedHomePage = () => {
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({ name: '', city: '', message: '' });
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });
  const [departureInfo, setDepartureInfo] = useState(null);

  // Container tracking state
  const [containerInput, setContainerInput] = useState('');
  const [trackedContainer, setTrackedContainer] = useState(null);
  const [containerLoading, setContainerLoading] = useState(false);
  const [containerError, setContainerError] = useState('');

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.adtexpress.fr/#organization",
      "name": "ADT Express",
      "url": "https://www.adtexpress.fr",
      "logo": "https://img.rocket.new/generatedImages/rocket_gen_img_1da41cc14-1765968552183.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+33651988621",
        "contactType": "Service Client",
        "areaServed": ["FR", "CI"],
        "availableLanguage": ["French"]
      },
      "sameAs": [
      "https://www.facebook.com/adtexpress",
      "https://www.instagram.com/adtexpress"]

    },
    {
      "@type": "LocalBusiness",
      "@id": "https://www.adtexpress.fr/#localbusiness",
      "name": "ADT Express",
      "image": "https://img.rocket.new/generatedImages/rocket_gen_img_1841552ea-1765478357017.png",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Paris",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 48.856614,
        "longitude": 2.3522219
      },
      "telephone": "+33651988621",
      "priceRange": "€€",
      "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }]

    },
    {
      "@type": "Service",
      "@id": "https://www.adtexpress.fr/#service",
      "serviceType": "Envoi colis Côte d\'Ivoire",
      "provider": {
        "@id": "https://www.adtexpress.fr/#organization"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Côte d\'Ivoire"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Services de transport maritime",
        "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Transport barrique noire 270L vers Abidjan – Offre Été 2026",
            "description": "Barrique noire 270L offerte + transport complet France → Abidjan. Offre valable jusqu'en septembre 2026."
          },
          "price": "160",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Transport barrique bleue 220L vers Abidjan",
            "description": "Transport maritime de barrique bleue 220 litres de Paris vers Abidjan"
          },
          "price": "180",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Transport conteneur France Côte d\'Ivoire",
            "description": "Fret maritime conteneur complet de la France vers la Côte d\'Ivoire"
          }
        }]

      }
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://www.adtexpress.fr/#breadcrumb",
      "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.adtexpress.fr"
      }]

    }]

  };

  // Countdown timer for next departure
  useEffect(() => {
    const loadDepartureInfo = async () => {
      try {
        const data = await nextDepartureService?.getActive();
        setDepartureInfo(data);
      } catch (error) {
        console.error('Error loading departure info:', error);
      }
    };

    loadDepartureInfo();
  }, []);

  // Countdown calculation with French date parsing
  useEffect(() => {
    if (!departureInfo?.departureDate) return;

    // French month names mapping (with and without accents)
    const frenchMonths = {
      'janvier': 0, 'février': 1, 'fevrier': 1, 'mars': 2, 'avril': 3,
      'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7, 'aout': 7,
      'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11, 'decembre': 11
    };

    const updateCountdown = () => {
      const now = new Date();
      const departureDateStr = departureInfo?.departureDate?.toLowerCase()?.trim();

      // Parse French date format: "28 février 2026" or "28 fevrier 2025"
      const dateMatch = departureDateStr?.match(/(\d{1,2})\s+(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)\s+(\d{4})/i);
      // Also try without year: "28 avril"
      const dateMatchNoYear = !dateMatch && departureDateStr?.match(/(\d{1,2})\s+(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)/i);

      if (dateMatch || dateMatchNoYear) {
        const match = dateMatch || dateMatchNoYear;
        const day = parseInt(match?.[1], 10);
        const monthName = match?.[2]?.toLowerCase()?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, ''); // Remove accents
        // If year is present use it, otherwise infer: use current year, bump to next year if date already passed
        let year = dateMatch ? parseInt(dateMatch?.[3], 10) : now?.getFullYear();

        // Find month index (handles both accented and non-accented versions)
        const monthIndex = frenchMonths?.[monthName];

        if (monthIndex !== undefined) {
          // Create departure date using the EXACT date from Supabase — no auto-adjustment
          const departureDate = new Date(year, monthIndex, day, 23, 59, 59);

          // If no year was provided and the date has already passed, try next year
          if (!dateMatch && departureDate < now) {
            departureDate?.setFullYear(year + 1);
          }

          const diff = departureDate - now;

          if (diff > 0) {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor(diff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
            const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));

            setCountdown({ days, hours, minutes });
          } else {
            setCountdown({ days: 0, hours: 0, minutes: 0 });
          }
        } else {
          console.error('Could not parse month from:', monthName);
          setCountdown({ days: 0, hours: 0, minutes: 0 });
        }
      } else {
        console.error('Could not parse departure date:', departureDateStr);
        setCountdown({ days: 0, hours: 0, minutes: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [departureInfo]);

  const barrelComparison = [
  {
    type: "Barrique Noire",
    icon: "Package",
    badge: "POPULAIRE",
    badgeColor: "bg-green-500",
    borderColor: "border-green-500",
    capacity: "270 litres",
    emptyPriceParis: "30€",
    emptyPriceBanlieue: "35€",
    transportPrice: "150€",
    totalParis: "180€",
    totalBanlieue: "185€",
    seoDescription: "Idéal pour les envois personnels vers Abidjan. Offre Été 2026 : 160€ TTC tout compris avec barrique offerte.",
    features: [
    "Plus grande capacité (270L)",
    "Idéale pour les gros volumes",
    "Offre Été 2026 : 160€ TTC tout compris",
    "Prix différencié Paris/Banlieue"]

  },
  {
    type: "Barrique Bleue",
    icon: "Box",
    badge: "RÉSISTANTE",
    badgeColor: "bg-blue-500",
    borderColor: "border-blue-500",
    capacity: "220 litres",
    emptyPriceParis: "40€",
    emptyPriceBanlieue: "45€",
    transportPrice: "140€",
    totalParis: "180€",
    totalBanlieue: "185€",
    seoDescription: "Solution économique pour expédier vos colis vers la Côte d\'Ivoire en groupage maritime.",
    features: [
    "Plus résistante",
    "Transport hors promo : 140€",
    "Prix unique toutes zones",
    "Capacité 220L"]

  }];


  const testimonials = [
  {
    name: "Aminata Koné",
    city: "Paris 18e",
    photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab434767-1765717415594.png",
    story: "J\'envoie des colis avec ADT Express depuis 3 ans. Ma famille à Abidjan reçoit tout en parfait état. Le service est professionnel et les prix sont justes.",
    rating: 5
  },
  {
    name: "Kouamé Diabaté",
    city: "Créteil (94)",
    photo: "https://img.rocket.new/generatedImages/rocket_gen_img_16a790691-1775074069330.png",
    story: "Excellent service ! J'ai envoyé 2 barriques pleines d'affaires pour ma mère. Tout est arrivé en 4 semaines, bien emballé. L'équipe est réactive.",
    rating: 5
  },
  {
    name: "Fatou Sangaré",
    city: "Saint-Denis (93)",
    photo: "https://images.unsplash.com/photo-1636057120639-dc84f7853e4e",
    story: "La collecte à domicile est vraiment pratique. Je remplis ma barrique tranquillement et ils viennent la chercher. C'est parfait pour nous qui travaillons.",
    rating: 5
  }];


  const faqItems = [
  {
    question: "Quel est le délai de livraison Paris-Abidjan ?",
    answer: "Le transport maritime entre Paris et Abidjan prend généralement entre 3 et 5 semaines selon les conditions de navigation. Nous organisons 2 départs par mois depuis Paris."
  },
  {
    question: "Quelle est la différence entre la barrique noire et la barrique bleue ?",
    answer: "La barrique noire a une capacité de 270 litres. Avec l'Offre Été 2026, elle est offerte avec le transport complet à 160€ TTC. Hors promo : 30€ (Paris) ou 35€ (banlieue) + 150€ transport. La barrique bleue (220L) est plus résistante : 40€ (Paris) ou 45€ (banlieue) + 140€ transport."
  },
  {
    question: "Peut-on envoyer de la nourriture dans les barriques ?",
    answer: "Oui, vous pouvez envoyer des denrées alimentaires non périssables comme du riz, des pâtes, des conserves, des épices, etc. Les produits frais ou périssables ne sont pas acceptés car le transport maritime dure plusieurs semaines."
  },
  {
    question: "Comment est calculé le prix du transport ?",
    answer: "Le prix du transport est forfaitaire par barrique, quel que soit le poids. Barrique noire hors promo : 150€. Barrique bleue : 140€. Avec l'Offre Été 2026, le transport de la barrique noire est inclus dans les 160€ TTC tout compris."
  },
  {
    question: "Livrez-vous partout en Côte d\'Ivoire ou seulement à Abidjan ?",
    answer: "La livraison standard se fait à Abidjan. Pour les livraisons dans d'autres villes de Côte d'Ivoire, contactez-nous pour un devis personnalisé. Nous pouvons organiser le transfert vers d'autres destinations selon vos besoins."
  },
  {
    question: "Quels documents sont nécessaires pour l'expédition ?",
    answer: "Vous aurez besoin d'une pièce d'identité. Pour certains articles spécifiques (électronique, médicaments), des documents supplémentaires peuvent être requis."
  },
  {
    question: "Puis-je suivre mon envoi en temps réel ?",
    answer: "Oui, dès la collecte de votre colis, vous recevez un numéro de référence ADT qui vous permet de suivre votre envoi à chaque étape : stockage, chargement, transit maritime, arrivée à Abidjan, dédouanement et livraison finale."
  },
  {
    question: "Que se passe-t-il si mon colis est endommagé ?",
    answer: "Tous nos envois sont assurés. En cas de dommage, vous devez nous signaler le problème dans les 48h suivant la réception. Nous procéderons à une expertise et vous serez indemnisé selon les termes de notre assurance tous risques."
  },
  {
    question: "Quels articles sont interdits à l\'expédition ?",
    answer: "Les articles interdits incluent : armes et munitions, drogues et substances illicites, produits périssables sans autorisation, matières dangereuses, contrefaçons, et articles contraires aux lois ivoiriennes. Contactez-nous pour toute question spécifique."
  },
  {
    question: "Proposez-vous un service d\'emballage ?",
    answer: "Oui, nous proposons un service d'emballage professionnel pour les articles fragiles ou volumineux. Nos équipes utilisent du matériel adapté (cartons renforcés, film bulle, sangles) pour garantir la sécurité de vos biens durant le transport."
  }];


  const processSteps = [
  {
    number: 1,
    title: "Choisissez votre barrique",
    description: "Noire ou bleue, selon vos besoins et votre budget.",
    icon: "ShoppingCart"
  },
  {
    number: 2,
    title: "Remplissez-la",
    description: "Mettez-y tout ce que vous souhaitez envoyer à vos proches.",
    icon: "Package"
  },
  {
    number: 3,
    title: "On s\'occupe du reste",
    description: "Nous collectons votre barrique à Paris ou en banlieue et l\'expédions par conteneur vers Abidjan.",
    icon: "Truck"
  },
  {
    number: 4,
    title: "Réception à Abidjan",
    description: "Vos proches sont livrés directement à leurs domiciles à Abidjan en toute sécurité.",
    icon: "Home"
  }];


  const whyChooseUs = [
  {
    icon: "Calendar",
    title: "Départs réguliers",
    description: "2 conteneurs par mois vers Abidjan pour une flexibilité maximale"
  },
  {
    icon: "DollarSign",
    title: "Tarifs transparents",
    description: "Des prix clairs et accessibles pour la diaspora ivoirienne, sans frais cachés"
  },
  {
    icon: "Truck",
    title: "Collecte à domicile",
    description: "Service de collecte à Paris intra-muros et en banlieue pour votre confort"
  },
  {
    icon: "MapPin",
    title: "Suivi en temps réel",
    description: "Suivez votre envoi du départ à l\'arrivée avec notre système de tracking"
  }];


  const handleTestimonialSubmit = (e) => {
    e?.preventDefault();
    alert('Merci pour votre témoignage ! Nous le traiterons prochainement.');
    setTestimonialForm({ name: '', city: '', message: '' });
    setShowTestimonialForm(false);
  };

  const handleContainerSearch = async (e) => {
    e?.preventDefault();
    const code = containerInput?.trim()?.toUpperCase();
    if (!code) {
      setContainerError('Veuillez entrer un numéro de conteneur');
      return;
    }
    setContainerError('');
    setContainerLoading(true);
    setTrackedContainer(null);
    try {
      const data = await containerService?.getByCode(code);
      setTrackedContainer(data);
    } catch (err) {
      setContainerError('Conteneur introuvable. Vérifiez le numéro saisi.');
    } finally {
      setContainerLoading(false);
    }
  };

  return (
    <>
      <SEOHelmet
        title="ADT Express - Transport Maritime France Côte d'Ivoire | Envoi Colis Abidjan"
        description="ADT Express : spécialiste du transport maritime de colis, barriques et électroménager entre la France et la Côte d'Ivoire. Envoi rapide et sécurisé vers Abidjan. Devis gratuit."
        keywords={"transport maritime côte d'ivoire, envoi colis abidjan, barrique abidjan, fret maritime france afrique, expédition côte d'ivoire, transport bagage abidjan, envoyer barrique Côte d'Ivoire, groupage maritime, tarif envoi Abidjan"}
        canonicalUrl="https://www.adtexpress.fr"
        ogImage="https://img.rocket.new/generatedImages/rocket_gen_img_1841552ea-1765478357017.png"
        structuredData={structuredData} />
      {/* Preload hero background image for LCP optimization */}
      <link
        rel="preload"
        as="image"
        href="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1920&q=85&fm=webp"
        type="image/webp" />
      <PromoStickBanner />
      <PublicNavigation />
      {/* Hero Section - AUTHORITY LOGISTICS AFRICA-EUROPE */}
      <section className="relative overflow-hidden pt-44 pb-32">
        {/* Full-width background image - NOT lazy-loaded for LCP */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=1920&q=85&fm=webp")',
            backgroundPosition: 'center 40%'
          }}
          aria-hidden="true" />
        
        {/* Dark overlay - 60% opacity black */}
        <div
          className="absolute inset-0 bg-black"
          style={{ opacity: 0.6 }}
          aria-hidden="true" />
        
        {/* Subtle grain texture overlay */}
        <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20200%20200%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cfilter%20id%3D%22noiseFilter%22%3E%3CfeTurbulence%20type%3D%22fractalNoise%22%20baseFrequency%3D%223%22%20numOctaves%3D%224%22%20stitchTiles%3D%22stitch%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20filter%3D%22url(%23noiseFilter)%22%2F%3E%3C%2Fsvg%3E")' }} />
        
        {/* Radial green halo behind tracking card */}
        <div className="absolute top-1/2 right-[15%] w-[500px] h-[500px] -translate-y-1/2 pointer-events-none hidden lg:block" style={{ background: 'radial-gradient(circle, rgba(22, 163, 74, 0.08) 0%, transparent 70%)' }} />
        
        <div className="relative z-10 container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            {/* LEFT COLUMN - 60% (3 cols) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="lg:col-span-3 space-y-8">
              
              {/* Main H1 Title - SEO Optimized - White text for contrast */}
              <h1
                className="text-[44px] md:text-[52px] tracking-tight"
                style={{ fontWeight: 700, color: '#FFFFFF', lineHeight: '1.25', letterSpacing: '-0.01em', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                Envoi colis et barriques vers la Côte d&apos;Ivoire depuis la France – Fret maritime Paris Abidjan
              </h1>
              
              {/* H2 Subtitle - SEO Optimized - Light gray for contrast */}
              <h2
                className="text-xl md:text-2xl max-w-[600px]"
                style={{ color: '#E5E7EB', fontSize: '20px', lineHeight: '1.6', fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
                Spécialiste du groupage maritime France – Abidjan. Expédition de colis, barriques et conteneurs avec suivi en temps réel. Deux départs par mois vers Abidjan.
              </h2>
              
              {/* Reassurance Paragraph - Lighter text */}
              <p
                className="text-base max-w-[600px]"
                style={{ color: '#D1D5DB', fontSize: '17px', lineHeight: '1.7', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                ADT Express accompagne la diaspora ivoirienne et les professionnels pour leurs envois vers la Côte d&apos;Ivoire. Dépôt en région parisienne, départ maritime sécurisé, arrivée au port d&apos;Abidjan avec transparence totale sur le suivi.
              </p>
              
              {/* CTA Buttons - Optimized */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/home-tracking">
                  <Button
                    size="lg"
                    className="text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-0.5"
                    style={{
                      backgroundColor: '#0E7C3F',
                      fontWeight: 600,
                      boxShadow: '0 12px 30px rgba(14,124,63,0.35)'
                    }}>
                    <Icon name="Search" className="w-5 h-5 mr-2" />
                    Suivre mon colis
                  </Button>
                </Link>
                <Link to="/dedicated-pricing-page">
                  <Button
                    size="lg"
                    className="bg-white hover:bg-green-50 font-medium px-8 py-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
                    style={{
                      border: '2px solid #16A34A',
                      color: '#16A34A',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}>
                    Envoyer une barrique
                  </Button>
                </Link>
              </div>

              {/* Reassurance Line with Icons - White text */}
              <div className="flex flex-wrap items-center gap-6 pt-4 text-sm" style={{ color: '#F3F4F6' }}>
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-400" />
                  <span className="font-medium">Départs 2 fois par mois</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-400" />
                  <span className="font-medium">Suivi en ligne 24/7</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-green-400" />
                  <span className="font-medium">Assistance France & Côte d&apos;Ivoire</span>
                </div>
              </div>
            </motion.div>

            {/* RIGHT COLUMN - 40% (2 cols) - TRACKING CARD MOCKUP - AUTHORITY MODE */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
              className="lg:col-span-2 hidden lg:block">
              
              {/* Premium Tracking Card - AUTHORITY ENHANCED */}
              <div
                className="bg-white p-10 relative overflow-hidden"
                style={{
                  borderRadius: '20px',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.12)',
                  border: '1px solid #0E7C3F'
                }}>
                
                {/* Subtle gradient accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-700 via-green-600 to-green-700" />
                
                {/* Card Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon name="Package" className="w-5 h-5 text-green-700" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Tracking en temps réel</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs font-medium text-green-700">Connexion sécurisée</span>
                  </div>
                </div>

                {/* Container Number Input */}
                <form onSubmit={handleContainerSearch} className="mb-6">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Ex: ADT-0426-CIV"
                        value={containerInput}
                        onChange={(e) => {
                          setContainerInput(e?.target?.value?.toUpperCase());
                          if (containerError) setContainerError('');
                        }}
                        className="w-full pl-9 pr-3 py-2.5 text-sm border-2 border-gray-200 rounded-lg focus:border-green-600 focus:ring-2 focus:ring-green-100 outline-none transition-all" />
                      
                    </div>
                    <button
                      type="submit"
                      disabled={containerLoading}
                      className="px-4 py-2.5 bg-green-700 hover:bg-green-800 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 flex items-center gap-1.5">
                      
                      {containerLoading ?
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :

                      <Icon name="ArrowRight" size={16} />
                      }
                      Voir
                    </button>
                  </div>
                  {containerError &&
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      {containerError}
                    </p>
                  }
                </form>

                {/* Status Badge - dynamic or static */}
                <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg mb-8 font-semibold ${trackedContainer ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'}`}>
                  <Icon name="Ship" className="w-5 h-5" />
                  <span className="text-sm tracking-wide">
                    {trackedContainer ? trackedContainer?.statusLabel?.toUpperCase() : 'EN TRANSIT MARITIME'}
                  </span>
                </div>

                {/* Shipment Details - dynamic or static */}
                <div className="space-y-5 mb-8">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Itinéraire</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {trackedContainer ? `${trackedContainer?.departurePort || 'Paris'} – ${trackedContainer?.destinationPort || 'Abidjan'}` : 'Paris – Abidjan'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Type d'envoi</span>
                    <span className="text-sm font-semibold text-gray-900">Groupage maritime</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Destination</span>
                    <span className="text-sm font-semibold text-gray-900">Côte d'Ivoire</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Conteneur</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {trackedContainer ? trackedContainer?.containerCode : 'ADT-0426-CIV'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-sm text-gray-600">Arrivée estimée</span>
                    <span className="text-sm font-semibold text-green-600">
                      {trackedContainer?.etaDate ?
                      new Date(trackedContainer?.etaDate)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) :
                      '21 Avril 2026'}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Paris</span>
                    <span>En mer</span>
                    <span>Abidjan</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: trackedContainer?.status === 'EN_TRANSIT_MARITIME' ? '65%' : trackedContainer?.status === 'ARRIVE_A_ABIDJAN' ? '100%' : trackedContainer?.status === 'AU_PORT_LE_HAVRE_ANVERS' ? '10%' : '65%' }} />
                  </div>
                </div>

                {/* Mini Timeline */}
                <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                  {trackedContainer?.departureDate ?
                  <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-900">Départ du port</p>
                        <p className="text-xs text-gray-500">
                          {new Date(trackedContainer?.departureDate)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                    </div> :

                  <>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-900">Départ du port du Havre</p>
                          <p className="text-xs text-gray-500">28 Mars 2026, 14:30</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-900">Collecte Paris 18e</p>
                          <p className="text-xs text-gray-500">20 Février 2026, 10:15</p>
                        </div>
                      </div>
                    </>
                  }
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Next Departure Section - INCREASED PADDING */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <NextDepartureCard countdown={countdown} propDepartureInfo={departureInfo} />
          </motion.div>
        </div>
      </section>
      {/* Promo Section - BETWEEN Hero and How It Works */}
      <PromoSection />
      {/* How It Works - Timeline - PREMIUM GRADIENT + ENHANCED STYLING */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20">
            
            <h2 className="text-4xl md:text-5xl mb-6" style={{ fontWeight: 700, color: '#0B1220', lineHeight: '1.25', letterSpacing: '-0.01em' }}>Comment ça marche ?</h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#6B7280', fontSize: '18px', lineHeight: '1.8' }}>Un processus simple en 4 étapes</p>
          </motion.div>

          {/* Horizontal Timeline */}
          <div className="relative">
            {/* Dotted Line - Hidden on mobile */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-0.5 border-t-2 border-dashed border-green-500" style={{ left: '12.5%', right: '12.5%' }} />
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
              {processSteps?.map((step, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative flex flex-col items-center text-center bg-white rounded-2xl p-8 transition-all duration-300 hover:scale-105 hover:-translate-y-2"
                style={{
                  boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                  border: '2px solid #16A34A'
                }}>
                  
                  {/* Circle with Number - ENHANCED SHADOW */}
                  <div className="relative z-10 w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-2xl mb-6" style={{ boxShadow: '0 10px 25px rgba(22,163,74,0.35)' }}>
                    {step?.number}
                  </div>
                  
                  {/* Icon */}
                  <div className="mb-5">
                    <Icon name={step?.icon} className="w-12 h-12 text-green-600" />
                  </div>
                  
                  {/* Title - BOLD */}
                  <h3 className="text-xl mb-4" style={{ fontWeight: 700, color: '#0B1220', lineHeight: '1.25', letterSpacing: '-0.01em' }}>{step?.title}</h3>
                  
                  {/* Description - GRAY */}
                  <p className="text-base" style={{ color: '#6B7280', fontSize: '18px', lineHeight: '1.7' }}>{step?.description}</p>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* Barrel Comparison - Pricing Cards - WHITE BACKGROUND + ENHANCED CARDS */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            
            {/* SEO-Optimized H2 Title */}
            <h2 className="text-3xl md:text-4xl mb-4" style={{ fontWeight: 700, color: '#0B1220', letterSpacing: '-0.01em' }}>Tarifs envoi colis et barriques Côte d&apos;Ivoire – Départ France</h2>
            
            {/* SEO-Optimized H3 Subtitle */}
            <h3 className="text-xl md:text-2xl mb-6" style={{ fontWeight: 600, color: '#374151', letterSpacing: '-0.005em' }}>Prix transport maritime Paris – Abidjan en groupage sécurisé</h3>
            
            {/* SEO Paragraph */}
            <p className="text-lg max-w-4xl mx-auto" style={{ color: '#6B7280', lineHeight: '1.8' }}>Découvrez nos tarifs transparents pour l&apos;envoi de colis, barriques et marchandises vers la Côte d&apos;Ivoire. Nos expéditions en groupage maritime depuis la France permettent de réduire les coûts tout en garantissant un transport sécurisé jusqu&apos;au port d&apos;Abidjan.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {barrelComparison?.map((barrel, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl border-2 ${barrel?.borderColor} overflow-hidden transition-all duration-300 hover:scale-105`}
              style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
              
              
                {/* Badge */}
                <div className={`absolute top-4 right-4 ${barrel?.badgeColor} text-white px-4 py-1 rounded-full text-xs font-bold z-10`} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                  {barrel?.badge}
                </div>
                
                {/* Header - INCREASED PADDING */}
                <div className="p-8 border-b">
                  <Icon name={barrel?.icon} className="w-12 h-12 text-gray-700 mb-5" />
                  <h3 className="text-2xl mb-3" style={{ fontWeight: 700, color: '#0B1220' }}>{barrel?.type}</h3>
                  <p className="text-lg" style={{ color: '#6B7280' }}>Capacité : {barrel?.capacity}</p>
                </div>
                
                {/* Pricing - INCREASED PADDING */}
                <div className="p-8 space-y-5">
                  <div className="flex justify-between items-center pb-4 border-b">
                    <span className="text-gray-700 font-medium">Barrique vide (Paris)</span>
                    <span className="font-bold text-xl text-green-600">{barrel?.emptyPriceParis}</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b">
                    <span className="text-gray-700 font-medium">Barrique vide (Banlieue)</span>
                    <span className="font-bold text-xl text-green-600">{barrel?.emptyPriceBanlieue}</span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b">
                    <span className="text-gray-700 font-medium">Transport vers Abidjan</span>
                    <span className="font-bold text-xl text-green-600">{barrel?.transportPrice}</span>
                  </div>
                  
                  {/* SEO Micro-text under each offer */}
                  {barrel?.seoDescription &&
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                      <p className="text-sm" style={{ color: '#374151', lineHeight: '1.6' }}>{barrel?.seoDescription}</p>
                    </div>
                }
                  
                  {/* Features - INCREASED SPACING */}
                  <ul className="space-y-4 pt-5">
                    {barrel?.features?.map((feature, idx) =>
                  <li key={idx} className="flex items-start gap-3">
                        <Icon name="Check" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700" style={{ lineHeight: '1.6' }}>{feature}</span>
                      </li>
                  )}
                  </ul>
                  
                  {/* Button - PREMIUM CTA */}
                  <div className="pt-7">
                    <Link to="/dedicated-pricing-page">
                      <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 rounded-xl transition-all duration-300 hover:scale-105" style={{ fontWeight: 600, boxShadow: '0 8px 20px rgba(22,163,74,0.25)' }}>
                        Commander
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Reassurance Line with Checkmarks */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 border border-green-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center gap-2">
                <Icon name="Check" className="w-6 h-6 text-green-600" />
                <p className="text-sm font-medium" style={{ color: '#374151' }}>Départ 2 fois par mois</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Icon name="Check" className="w-6 h-6 text-green-600" />
                <p className="text-sm font-medium" style={{ color: '#374151' }}>Livraison au port d&apos;Abidjan</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Icon name="Check" className="w-6 h-6 text-green-600" />
                <p className="text-sm font-medium" style={{ color: '#374151' }}>Suivi en ligne en temps réel</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Icon name="Check" className="w-6 h-6 text-green-600" />
                <p className="text-sm font-medium" style={{ color: '#374151' }}>Assistance France & Côte d&apos;Ivoire</p>
              </div>
            </div>
          </motion.div>
          
          {/* SEO CTA at Bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center">
            <p className="text-lg mb-6" style={{ color: '#374151', fontWeight: 500 }}>Besoin d&apos;un devis pour un envoi vers la Côte d&apos;Ivoire ? Contactez-nous dès maintenant.</p>
            <Link to="/dedicated-pricing-page">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white font-semibold px-10 py-4 rounded-xl transition-all duration-300 hover:scale-105" style={{ fontWeight: 600, boxShadow: '0 8px 20px rgba(22,163,74,0.25)' }}>
                <Icon name="MessageCircle" className="w-5 h-5 mr-2" />
                Demander un devis
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
      {/* Why Choose Us - Features - PREMIUM GRADIENT + ENHANCED STYLING */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20">
            
            <h2 className="text-4xl md:text-5xl mb-6" style={{ fontWeight: 700, color: '#0B1220', lineHeight: '1.25', letterSpacing: '-0.01em' }}>Pourquoi choisir ADT Express ?</h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#6B7280', fontSize: '18px', lineHeight: '1.8' }}>Les avantages qui font la différence</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {whyChooseUs?.map((item, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-10 transition-all duration-300 hover:scale-105 hover:-translate-y-2"
              style={{
                boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                border: '2px solid #0E7C3F'
              }}>
              
              
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 8px 20px rgba(22,163,74,0.2)' }}>
                    <Icon name={item?.icon} className="w-9 h-9 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl mb-4" style={{ fontWeight: 700, color: '#0B1220', lineHeight: '1.25', letterSpacing: '-0.01em' }}>
                      {item?.title}
                    </h3>
                    <p className="leading-relaxed" style={{ color: '#6B7280', fontSize: '18px', lineHeight: '1.7' }}>
                      {item?.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      {/* Trust Signals - PREMIUM GRADIENT + ENHANCED STYLING */}
      <section className="py-24 px-6" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20">
            
            <h2 className="text-4xl md:text-5xl mb-6" style={{ fontWeight: 700, color: '#0B1220', lineHeight: '1.25', letterSpacing: '-0.01em' }}>Votre confiance, notre priorité</h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#6B7280', fontSize: '18px', lineHeight: '1.8' }}>Des garanties solides pour vos expéditions</p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
            {
              icon: "Shield",
              title: "Sécurisé & Assuré",
              description: "Tous vos envois sont assurés et suivis"
            },
            {
              icon: "Clock",
              title: "Livraison Rapide",
              description: "Délais respectés et transparents"
            },
            {
              icon: "Award",
              title: "Service Certifié",
              description: "Entreprise française agréée"
            },
            {
              icon: "Users",
              title: "Support Dédié",
              description: "Équipe disponible 7j/7"
            }]?.map((signal, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 text-center transition-all duration-300 hover:scale-105 hover:-translate-y-2"
              style={{
                boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
                border: '2px solid #16A34A'
              }}>
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-6" style={{ boxShadow: '0 8px 20px rgba(22,163,74,0.2)' }}>
                  <Icon name={signal?.icon} className="w-9 h-9 text-green-600" />
                </div>
                <h3 className="text-lg mb-3" style={{ fontWeight: 700, color: '#0B1220', lineHeight: '1.25', letterSpacing: '-0.01em' }}>
                  {signal?.title}
                </h3>
                <p className="text-base" style={{ color: '#6B7280', fontSize: '18px', lineHeight: '1.7' }}>
                  {signal?.description}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      {/* FAQ Section - LIGHT GRAY + ENHANCED CARDS */}
      <section className="py-20 px-6" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            
            <h2 className="text-3xl md:text-4xl mb-5" style={{ fontWeight: 700, color: '#0B1220', letterSpacing: '-0.01em' }}>Questions Fréquentes</h2>
            <p className="text-lg" style={{ color: '#6B7280', lineHeight: '1.8' }}>Tout ce que vous devez savoir sur nos services</p>
          </motion.div>

          <div className="space-y-4">
            {faqItems?.map((item, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl overflow-hidden transition-all duration-300"
              style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.04)' }}>
              
              
                <button
                onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                className="w-full px-7 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-all duration-300">
                
                  <span className="font-semibold pr-4" style={{ color: '#0B1220', fontWeight: 600 }}>{item?.question}</span>
                  <Icon
                  name={expandedFAQ === index ? "ChevronUp" : "ChevronDown"}
                  className="w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-300" />
                
                </button>
                {expandedFAQ === index &&
              <div className="px-7 pb-6 leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.7' }}>
                    {item?.answer}
                  </div>
              }
              </motion.div>
            )}
          </div>
        </div>
      </section>
      {/* Quote Calculator - WHITE BACKGROUND */}
      <section id="calculator" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            
            <h2 className="text-3xl md:text-4xl mb-5" style={{ fontWeight: 700, color: '#0B1220', letterSpacing: '-0.01em' }}>Calculez votre devis</h2>
            <p className="text-lg" style={{ color: '#6B7280', lineHeight: '1.8' }}>Obtenez une estimation instantanée pour votre envoi</p>
          </motion.div>
          <QuoteCalculator />
        </div>
      </section>
      {/* SEO Internal Linking Section */}
      <section className="py-16 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-green-50 to-gray-50 rounded-2xl p-8 md:p-12 border border-green-100">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              En savoir plus sur notre logistique France – Côte d'Ivoire
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              ADT Express vous accompagne dans toutes vos expéditions maritimes entre la France et la Côte d'Ivoire. 
              Que vous soyez particulier ou professionnel, découvrez nos solutions de <strong>groupage maritime France Côte d'Ivoire</strong> et 
              notre service de <strong>fret maritime Paris Abidjan</strong> adapté à vos besoins.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Link
                to="/dedicated-pricing-page"
                className="flex items-start gap-4 p-5 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-green-500 group">
                <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-600 transition-colors">
                  <Icon name="DollarSign" className="w-6 h-6 text-green-600 group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    Voir nos tarifs maritimes
                  </h3>
                  <p className="text-sm text-gray-600">
                    Consultez nos prix compétitifs pour l&apos;<strong>envoi colis Côte d&apos;Ivoire</strong> et le <strong>transport conteneur Abidjan</strong>
                  </p>
                </div>
              </Link>

              <Link
                to="/professional-services-landing-page"
                className="flex items-start gap-4 p-5 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-green-500 group">
                <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-600 transition-colors">
                  <Icon name="Briefcase" className="w-6 h-6 text-green-600 group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    Solutions logistiques pour professionnels
                  </h3>
                  <p className="text-sm text-gray-600">
                    Découvrez nos offres dédiées aux importateurs et entreprises
                  </p>
                </div>
              </Link>

              <Link
                to="/shipping-information"
                className="flex items-start gap-4 p-5 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-green-500 group">
                <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-600 transition-colors">
                  <Icon name="Ship" className="w-6 h-6 text-green-600 group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    Fonctionnement du transport maritime
                  </h3>
                  <p className="text-sm text-gray-600">
                    Comprenez notre processus de bout en bout
                  </p>
                </div>
              </Link>

              <Link
                to="/maritime-transport-blog-hub"
                className="flex items-start gap-4 p-5 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-green-500 group">
                <div className="bg-green-100 p-3 rounded-lg group-hover:bg-green-600 transition-colors">
                  <Icon name="BookOpen" className="w-6 h-6 text-green-600 group-hover:text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    Conseils et actualités logistiques
                  </h3>
                  <p className="text-sm text-gray-600">
                    Restez informé sur le transport maritime vers l&apos;Afrique
                  </p>
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Contact Section - LIGHT GRAY */}
      <section className="py-20 px-6" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="container mx-auto max-w-6xl">
          <ContactSection />
        </div>
      </section>
      {/* WhatsApp Floating Button - ENHANCED */}
      <a
        href="https://wa.me/33651988621"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 transition-all duration-300 z-50 group hover:scale-110 hover:-translate-y-1"
        aria-label="Contactez-nous sur WhatsApp"
        style={{ boxShadow: '0 10px 30px rgba(34,197,94,0.4)' }}>
        
        <Icon name="MessageCircle" className="w-7 h-7" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
          Contactez-nous sur WhatsApp
        </span>
      </a>
      {/* Chat Widget */}
      <ChatWidget />
    </>);









};

export default EnhancedSEOOptimizedHomePage;